import { useState, useRef } from "react";
import { ReactSketchCanvas, ReactSketchCanvasRef } from "react-sketch-canvas";
import { Slider } from "../ui/slider";
import { cn } from "@/lib/utils";
import { IoMdDownload, IoMdBrush } from "react-icons/io";
import { FaEraser } from "react-icons/fa";
import { RiDeleteBin2Fill } from "react-icons/ri";
import { MdOutlineRedo, MdOutlineUndo } from "react-icons/md";
import useMethodStore from "@/store/MethodStore";
import TooltipContainer from "../TooltipContainer";
import { RxDragHandleDots2 } from "react-icons/rx";

const buttonStyling = "h-9 w-9 flex justify-center items-center bg-zinc-800 hover:bg-zinc-800/60 border-1 border-zinc-700 text-white rounded-md"

export default function Paint() {
  const [brushRadius, setBrushRadius] = useState(5);
  const [brushColor, setBrushColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#f1f1f1");
  const [isEraser, setIsEraser] = useState(false); // Track whether the eraser is selected

  const paintState = useMethodStore(state => state.paintState)

  // Define the ref with the proper type for ReactSketchCanvas
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const isSmallScreen = window.matchMedia("(max-width: 1024px)").matches

  const saveCanvasAsImage = async () => {
    if (canvasRef.current) {
      const dataURL = canvasRef.current.exportImage("png");
      const link = document.createElement("a");
      link.href = await dataURL;
      link.download = "drawing.png";
      link.click();
    }
  };

  const toggleEraser = () => {
    canvasRef.current?.eraseMode(!isEraser);
    setIsEraser(!isEraser);
  };

  return (
    <div className={`relative ${paintState === "maximized" && "h-full"} ${isSmallScreen && "h-full w-full"}`}>
      <ReactSketchCanvas
        ref={canvasRef}
        strokeColor={brushColor} // Use white for eraser
        strokeWidth={brushRadius} // Adjust size for eraser
        eraserWidth={brushRadius * 2}
        canvasColor={backgroundColor}
        height={(paintState === "maximized" || isSmallScreen) ? "100%" : "700px"}
        width={(paintState === "maximized" || isSmallScreen) ? "100%" : "1000px"}
      />

      <div className="absolute bottom-4 pr-10 md:pr-3 lg:bottom-2 left-1/2 transform -translate-x-1/2 text-zinc-100 bg-zinc-800/90 flex flex-col sm:flex-row space-y-4 sm:space-y-0 pt-4 sm:pt-2 justify-center items-center sm:space-x-4 py-2 px-3 rounded-md">
        <div className="w-full">
          <Slider
            defaultValue={[10]}
            max={40}
            min={1}
            step={1}
            value={[brushRadius]}
            onValueChange={(value) => setBrushRadius(value[0])}
            className={cn("w-full sm:w-40", "")}
          />
        </div>
        <div className="flex justify-center items-center space-x-1">
          <label htmlFor="brushColor" style={{ backgroundColor: brushColor }} className="w-8 h-8 rounded-md">
            <input
              type="color"
              id="brushColor"
              value={brushColor}
              onChange={(e) => setBrushColor(e.target.value)}
              className="h-8 w-8 opacity-0 rounded-md overflow-hidden !border-none !outline-none p-0 m-0"
            />
          </label>
          <label htmlFor="backgroundColor" style={{ backgroundColor: backgroundColor }} className="w-8 h-8 rounded-md">
            <input
              type="color"
              id="backgroundColor"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="h-8 w-8 opacity-0 rounded-md overflow-hidden !border-none !outline-none p-0 m-0"
            />
          </label>
          <TooltipContainer className="mb-3" tooltip="Toggle Eraser">
            <div
              onClick={toggleEraser}
              className={cn(
                "h-9 w-9 flex justify-center items-center bg-zinc-800 border-1 border-zinc-700 text-white rounded-md",
                isEraser ? "bg-red-600 hover:bg-red-700" : "hover:bg-zinc-800/60"// Highlight when eraser is selected
              )}
            >
              {isEraser ? <FaEraser /> : <IoMdBrush />} {/* Conditionally render Eraser or Brush */}
            </div>
          </TooltipContainer>
          <TooltipContainer className="mb-3" tooltip="Undo">
            <div
              onClick={() => canvasRef.current?.undo()}
              className={buttonStyling}>
              <MdOutlineUndo />
            </div>
          </TooltipContainer>
          <TooltipContainer className="mb-3" tooltip="Redo">
            <div
              onClick={() => canvasRef.current?.redo()}
              className={buttonStyling}
            >
              <MdOutlineRedo />
            </div>
          </TooltipContainer>
          <TooltipContainer className="mb-3" tooltip="Clear Canvas">
            <div
              onClick={saveCanvasAsImage}
              className={buttonStyling}
            >
              <IoMdDownload />
            </div>
          </TooltipContainer>
          <TooltipContainer className="mb-3" tooltip="Clear Canvas">
            <div
              onClick={() => canvasRef.current?.clearCanvas()}
              className={buttonStyling}
            >
              <RiDeleteBin2Fill />
            </div>
          </TooltipContainer>
        </div>
        <div className="absolute !mt-0 md:hidden h-full w-10 text-zinc-400 flex justify-center items-center text-3xl top-0 right-0">
          <RxDragHandleDots2 />
        </div>
      </div>
    </div>
  );
}
